import "./ClientSurgicalHistoryFormComponent.scss";
import * as Yup from "yup";
import {IClientSurgicalHistoryForm} from "../../../../shared/models/client.model";
import React, {useCallback, useEffect, useState} from "react";
import _ from "lodash";
import {Field, FieldProps, Form, Formik, FormikHelpers} from "formik";
import {CommonService} from "../../../../shared/services";
import {IAPIResponseType} from "../../../../shared/models/api.model";
import {Misc} from "../../../../constants";
import CardComponent from "../../../../shared/components/card/CardComponent";
import ButtonComponent from "../../../../shared/components/button/ButtonComponent";
import {useDispatch, useSelector} from "react-redux";
import {IRootReducerState} from "../../../../store/reducers";
import {ISurgicalHistoryOption} from "../../../../shared/models/common.model";
import CheckBoxComponent from "../../../../shared/components/form-controls/check-box/CheckBoxComponent";
import FormikTextAreaComponent
    from "../../../../shared/components/form-controls/formik-text-area/FormikTextAreaComponent";
import FormikCheckBoxComponent
    from "../../../../shared/components/form-controls/formik-check-box/FormikCheckBoxComponent";
import LoaderComponent from "../../../../shared/components/loader/LoaderComponent";
import StatusCardComponent from "../../../../shared/components/status-card/StatusCardComponent";
import HeadingWithNavigationComponent
    from "../../../../shared/components/heading-with-navigation/HeadingWithNavigationComponent";
import DetailsViewCardComponent from "../../../../shared/components/details-view-card/DetailsViewCardComponent";
import AcknowledgementFormComponent from "../../acknowledgement-form/AcknowledmentFormComponent";
import ModalComponent from "../../../../shared/components/modal/ModalComponent";

interface ClientSurgicalHistoryFormComponentProps {
    onCancel: () => void;
    onNext?: () => void;
    clientId: string;
    onSave: (clientSurgicalHistoryDetails: any) => void;
    clientMedicalHistoryDetails: any;
    isClientMedicalHistoryDetailsLoaded: boolean | undefined;
    isClientMedicalHistoryDetailsLoading: boolean | undefined;
    isClientMedicalHistoryDetailsLoadingFailed: boolean | undefined;
}

const ClientSurgicalHistoryValidationSchema = Yup.object({
    surgical_history: Yup.object({
        isCustomOption: Yup.boolean().nullable(),
        questions: Yup.array().nullable().when("isCustomOption", {
            is: false,
            then: Yup.array().min(1, 'Surgical history is required'),
            otherwise: Yup.array().nullable()
        }),
        comments: Yup.string().when("isCustomOption", {
            is: true,
            then: Yup.string().required('Comments is required'),
            otherwise: Yup.string().nullable()
        })
    }),
});

const ClientSurgicalHistoryInitialValues: IClientSurgicalHistoryForm = {
    surgical_history: {
        questions: [],
        isCustomOption: false,
        comments: ""
    }
};

const ClientSurgicalHistoryFormComponent = (props: ClientSurgicalHistoryFormComponentProps) => {

    const {
        onCancel, onSave,
        clientId,
        clientMedicalHistoryDetails,
        isClientMedicalHistoryDetailsLoaded,
        isClientMedicalHistoryDetailsLoading,
        isClientMedicalHistoryDetailsLoadingFailed
    } = props;
    const {surgicalHistoryOptionsList} = useSelector((state: IRootReducerState) => state.staticData);
    const [clientSurgicalHistoryInitialValues, setClientSurgicalHistoryInitialValues] = useState<IClientSurgicalHistoryForm>(_.cloneDeep(ClientSurgicalHistoryInitialValues));
    const [isClientSurgicalHistorySavingInProgress, setIsClientSurgicalHistorySavingInProgress] = useState(false);
    const dispatch = useDispatch();
    const [isAcknowledgementModalOpen, setIsAcknowledgementModalOpen] = useState(false);

    const onSubmit = useCallback((values: any, {setErrors}: FormikHelpers<any>) => {
        setClientSurgicalHistoryInitialValues(values);
        setIsAcknowledgementModalOpen(true);
    }, [])

    const handleAcknowledgementFormSubmit = useCallback((acknowledgementValues:any) => {
        const payload = {
            ...CommonService.removeKeysFromJSON(_.cloneDeep(clientSurgicalHistoryInitialValues), ['questions_details']),
            ...acknowledgementValues,
            client_id: clientId
        };
        setIsClientSurgicalHistorySavingInProgress(true);
        CommonService._client.ClientMedicalHistorySurgicalHistoryDetailsSaveAPICall(payload)
            .then((response: IAPIResponseType<IClientSurgicalHistoryForm>) => {
                CommonService._alert.showToast(response[Misc.API_RESPONSE_MESSAGE_KEY], "success");
                setIsClientSurgicalHistorySavingInProgress(false);
                setClientSurgicalHistoryInitialValues(_.cloneDeep(clientSurgicalHistoryInitialValues));
                onSave(response);
            })
            .catch((error: any) => {
                CommonService.handleErrors(error[Misc.API_RESPONSE_ERROR_KEY], 'error');
                setIsClientSurgicalHistorySavingInProgress(false);
            })
    }, [onSave,clientId,clientSurgicalHistoryInitialValues]);

    useEffect(() => {
        if (clientMedicalHistoryDetails) {
            if (clientMedicalHistoryDetails?.surgical_history?.comments) {
                clientMedicalHistoryDetails.surgical_history.isCustomOption = true;
            }
            setClientSurgicalHistoryInitialValues({
                surgical_history: clientMedicalHistoryDetails?.surgical_history
            });
        }
    }, [dispatch, clientMedicalHistoryDetails]);

    const handleSurgicalHistoryOptionSelection = useCallback((optionId: string, selectedOptions: string[]) => {
        const options = _.cloneDeep(selectedOptions);
        const index = options?.findIndex((value: string) => value === optionId);
        if (index > -1) {
            options.splice(index, 1);
        } else {
            options.push(optionId);
        }
        return options;
    }, []);

    return (
        <div className={'client-surgical-history-form-component'}>
            < >
                {
                    isClientMedicalHistoryDetailsLoading && <div>
                        <LoaderComponent/>
                    </div>
                }
                {
                    isClientMedicalHistoryDetailsLoadingFailed &&
                    <StatusCardComponent title={"Failed to fetch medical Details"}/>
                }
            </>
            {isClientMedicalHistoryDetailsLoaded &&
                <>
                    <HeadingWithNavigationComponent onBackNavigation={onCancel}
                                                    title={"Back"}/>

                    <Formik
                        validationSchema={ClientSurgicalHistoryValidationSchema}
                        initialValues={clientSurgicalHistoryInitialValues}
                        onSubmit={onSubmit}
                        validateOnChange={false}
                        validateOnBlur={true}
                        enableReinitialize={true}
                        validateOnMount={true}>
                        {({
                              values,
                              errors,
                              setFieldTouched,
                              setFieldValue,
                              setFieldError,
                              isValid,
                              validateForm
                          }) => {
                            // eslint-disable-next-line react-hooks/rules-of-hooks
                            useEffect(() => {
                                validateForm();
                            }, [validateForm, values]);
                            return (<CardComponent>
                                    <div className={'edit-surgical-history-heading'}>
                                            Edit Surgical History
                                    </div>
                                    <Form noValidate={true} className={"t-form"}>
                                        <div className={'edit-surgical-history-wrapper'}>
                                            <DetailsViewCardComponent title={"Surgical History"}>
                                                <div className={'edit-surgical-history-sub-heading'}>
                                                    Have you ever had:
                                                </div>
                                                <div className={'d-flex'}>
                                                    <div>
                                                        {
                                                            surgicalHistoryOptionsList?.slice(0, 9)?.map((option: ISurgicalHistoryOption) => {
                                                                return <div key={option._id}>
                                                                    <Field
                                                                        name={"surgical_history.questions"}>
                                                                        {(field: FieldProps) => (
                                                                            <CheckBoxComponent
                                                                                id={'cb_' + option?.title}
                                                                                label={option?.title}
                                                                                // required={true}
                                                                                checked={field.field?.value?.indexOf(option._id) > -1}
                                                                                onChange={() => {
                                                                                    setFieldTouched(field.field?.name);
                                                                                    setFieldValue(field.field?.name, handleSurgicalHistoryOptionSelection(option._id, field.field?.value));
                                                                                    validateForm();
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </Field>
                                                                </div>
                                                            })
                                                        }
                                                    </div>
                                                    <div className={'ts-col-3'}/>
                                                    <div>
                                                        {
                                                            surgicalHistoryOptionsList?.slice(9, 18)?.map((option: ISurgicalHistoryOption) => {
                                                                return <div key={option._id}>
                                                                    <Field
                                                                        name={"surgical_history.questions"}>
                                                                        {(field: FieldProps) => (
                                                                            <CheckBoxComponent
                                                                                id={'cb_' + option?.title}
                                                                                label={option?.title}
                                                                                // required={true}
                                                                                checked={field.field?.value?.indexOf(option._id) > -1}
                                                                                onChange={() => {
                                                                                    setFieldTouched(field.field?.name);
                                                                                    setFieldValue(field.field?.name, handleSurgicalHistoryOptionSelection(option._id, field.field?.value));
                                                                                    validateForm();
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </Field>
                                                                </div>
                                                            })
                                                        }
                                                    </div>
                                                </div>
                                                <div className="ts-row">
                                                    <div className="ts-col-5">
                                                        <Field
                                                            name={"surgical_history.isCustomOption"}>
                                                            {(field: FieldProps) => (
                                                                <FormikCheckBoxComponent
                                                                    id={"other_cb"}
                                                                    formikField={field}
                                                                    label={"Other Surgery not Listed?"}
                                                                    onChange={(isChecked) => {
                                                                        if (!isChecked) {
                                                                            setFieldValue('surgical_history.comments', "");
                                                                            setFieldError('surgical_history.comments', undefined);
                                                                            setTimeout(() => { // TODO solve fool proof
                                                                                validateForm();
                                                                            }, 10);
                                                                        }
                                                                    }}
                                                                />
                                                            )}
                                                        </Field>
                                                    </div>
                                                </div>
                                                {values.surgical_history?.isCustomOption && <div className="ts-row">
                                                    <div className="ts-col-10">
                                                        <Field name={`surgical_history.comments`}>
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikTextAreaComponent
                                                                        id={"comments"}
                                                                        label={"Other Illnesses/Conditions"}
                                                                        placeholder={"Enter your comments here"}
                                                                        disabled={!values.surgical_history?.isCustomOption}
                                                                        required={values.surgical_history?.isCustomOption}
                                                                        formikField={field}
                                                                        fullWidth={true}
                                                                        rows={5}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </div>
                                                </div>}
                                            </DetailsViewCardComponent>
                                        </div>
                                        <div className="t-form-actions mrg-bottom-0 mrg-top-60">
                                            <ButtonComponent
                                                id={"home_btn"}
                                                variant={"outlined"}
                                                onClick={onCancel}
                                                disabled={isClientSurgicalHistorySavingInProgress}
                                                color={'accent'}
                                            >
                                                Cancel
                                            </ButtonComponent>&nbsp;
                                            <ButtonComponent
                                                id={"save_next_btn"}
                                                className={'mrg-left-25'}
                                                isLoading={isClientSurgicalHistorySavingInProgress}
                                                disabled={isClientSurgicalHistorySavingInProgress || !isValid || CommonService.isEqual(values, clientSurgicalHistoryInitialValues)}
                                                type={"submit"}
                                                color={'accent'}
                                            >
                                                {/*{isClientSurgicalHistorySavingInProgress ? "Saving" : "Save"}*/}
                                                Next
                                            </ButtonComponent>
                                        </div>
                                    </Form>
                                </CardComponent>
                            )
                        }}
                    </Formik>
                </>
            }
            <ModalComponent isOpen={isAcknowledgementModalOpen}>
                <AcknowledgementFormComponent setIsAcknowledgementModalOpen={setIsAcknowledgementModalOpen}
                                              isLoading={isClientSurgicalHistorySavingInProgress}
                                              onSubmit={handleAcknowledgementFormSubmit}/>
            </ModalComponent>
        </div>
    )
        ;

};

export default ClientSurgicalHistoryFormComponent;
